import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Tab,
  Tabs,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  InputAdornment
} from '@mui/material';
import { Visibility, VisibilityOff, Save, Lock } from '@mui/icons-material';
import { api } from '../services/api';
import { scrollToFirstError, handleApiError } from '../utils/formErrorHandling';

interface User {
  id: number;
  name: string;
  email: string;
  username: string;
  job_title: string;
  contact_phone: string;
  address_1: string;
  address_2: string;
  city: string;
  state: string;
  zip_code: string;
  country: string;
  pan_no: string;
  currency: string;
  billing_currency: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const ProfileEdit: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' | 'warning' | 'info' });
  const [isPmpAdmin, setIsPmpAdmin] = useState(false);
  
  // Field-specific error states
  const [profileFieldErrors, setProfileFieldErrors] = useState<Record<string, string[]>>({});
  const [passwordFieldErrors, setPasswordFieldErrors] = useState<Record<string, string[]>>({});
  
  // Refs for form fields to enable scrolling
  const profileFieldRefs = useRef<Record<string, HTMLElement | null>>({});
  const passwordFieldRefs = useRef<Record<string, HTMLElement | null>>({});

  // Profile form state
  const [profileForm, setProfileForm] = useState({
    name: '',
    email: '',
    username: '',
    job_title: '',
    contact_phone: '',
    address_1: '',
    address_2: '',
    city: '',
    state: '',
    zip_code: '',
    country: '',
    pan_no: '',
    currency: 'USD',
    billing_currency: 'USD'
  });

  // Password form state
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });

  const currencies = [
    { value: 'USD', label: 'USD - US Dollar' },
    { value: 'EUR', label: 'EUR - Euro' },
    { value: 'GBP', label: 'GBP - British Pound' },
    { value: 'INR', label: 'INR - Indian Rupee' },
    { value: 'JPY', label: 'JPY - Japanese Yen' }
  ];

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/profile');
      const userData = response.data.data.user;
      const userRoles = response.data.data.roles || [];
      
      setUser(userData);
      
      // Check if user is PMP admin
      const isAdmin = userRoles.some((role: any) => role.slug === 'pmp_admin') || userData.role === 'pmp_admin';
      setIsPmpAdmin(isAdmin);
      
      setProfileForm({
        name: userData.name || '',
        email: userData.email || '',
        username: userData.username || '',
        job_title: userData.job_title || '',
        contact_phone: userData.contact_phone || '',
        address_1: userData.address_1 || '',
        address_2: userData.address_2 || '',
        city: userData.city || '',
        state: userData.state || '',
        zip_code: userData.zip_code || '',
        country: userData.country || '',
        pan_no: userData.pan_no || '',
        currency: userData.currency || 'USD',
        billing_currency: userData.billing_currency || 'USD'
      });
    } catch (error) {
      console.error('Error fetching profile:', error);
      setSnackbar({ open: true, message: 'Failed to load profile', severity: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Helper function to show enhanced toast messages
  const showToast = (message: string, severity: 'error' | 'warning' | 'info' | 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleProfileChange = (field: string, value: string) => {
    setProfileForm(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (profileFieldErrors[field]) {
      setProfileFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordForm(prev => ({ ...prev, [field]: value }));
    
    // Clear field error when user starts typing
    if (passwordFieldErrors[field]) {
      setPasswordFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const togglePasswordVisibility = (field: 'current' | 'new' | 'confirm') => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleProfileSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Only allow PMP admins to update profile information
    if (!isPmpAdmin) {
      setSnackbar({ open: true, message: 'Only PMP admins can update profile information', severity: 'error' });
      return;
    }
    
    // Clear previous errors
    setProfileFieldErrors({});
    setSaving(true);

    try {
      await api.put('/profile', profileForm);
      setSnackbar({ open: true, message: 'Profile updated successfully', severity: 'success' });
      fetchProfile(); // Refresh data
    } catch (error: any) {
      console.error('Error updating profile:', error);
      
      handleApiError(
        error,
        setProfileFieldErrors,
        (message) => setSnackbar({ open: true, message, severity: 'error' }),
        profileFieldRefs,
        'Failed to update profile',
        showToast
      );
    } finally {
      setSaving(false);
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Clear previous errors
    setPasswordFieldErrors({});
    setSaving(true);

    try {
      await api.post('/profile/change-password', passwordForm);
      setSnackbar({ open: true, message: 'Password changed successfully', severity: 'success' });
      setPasswordForm({
        current_password: '',
        new_password: '',
        new_password_confirmation: ''
      });
    } catch (error: any) {
      console.error('Error changing password:', error);
      
      handleApiError(
        error,
        setPasswordFieldErrors,
        (message) => setSnackbar({ open: true, message, severity: 'error' }),
        passwordFieldRefs,
        'Failed to change password',
        showToast
      );
    } finally {
      setSaving(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  if (loading) {
    return (
      <Container maxWidth="md">
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          <Typography>Loading profile...</Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="md">
      <Box sx={{ mt: 4, mb: 4 }}>
        <Paper sx={{ width: '100%' }}>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} aria-label="profile tabs">
              <Tab label="Profile Information" />
              <Tab label="Change Password" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Typography variant="h5" gutterBottom>
              Profile Information
            </Typography>
            {!isPmpAdmin && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Profile information can only be edited by PMP administrators. You can change your password in the "Change Password" tab.
              </Alert>
            )}
            <form onSubmit={handleProfileSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <TextField
                      required
                      fullWidth
                      label="Full Name"
                      value={profileForm.name}
                      onChange={(e) => handleProfileChange('name', e.target.value)}
                      disabled={!isPmpAdmin}
                      error={!!profileFieldErrors.name}
                      helperText={profileFieldErrors.name ? profileFieldErrors.name[0] : ''}
                      inputRef={(el) => profileFieldRefs.current.name = el}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <TextField
                      required
                      fullWidth
                      label="Email"
                      type="email"
                      value={profileForm.email}
                      onChange={(e) => handleProfileChange('email', e.target.value)}
                      disabled={!isPmpAdmin}
                      error={!!profileFieldErrors.email}
                      helperText={profileFieldErrors.email ? profileFieldErrors.email[0] : ''}
                      inputRef={(el) => profileFieldRefs.current.email = el}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <TextField
                      fullWidth
                      label="Username"
                      value={profileForm.username}
                      onChange={(e) => handleProfileChange('username', e.target.value)}
                      disabled={!isPmpAdmin}
                      error={!!profileFieldErrors.username}
                      helperText={profileFieldErrors.username ? profileFieldErrors.username[0] : ''}
                      inputRef={(el) => profileFieldRefs.current.username = el}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <TextField
                      fullWidth
                      label="Job Title"
                      value={profileForm.job_title}
                      onChange={(e) => handleProfileChange('job_title', e.target.value)}
                      disabled={!isPmpAdmin}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <TextField
                      fullWidth
                      label="Contact Phone"
                      value={profileForm.contact_phone}
                      onChange={(e) => handleProfileChange('contact_phone', e.target.value)}
                      disabled={!isPmpAdmin}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <FormControl fullWidth>
                      <InputLabel>Currency</InputLabel>
                      <Select
                        value={profileForm.currency}
                        label="Currency"
                        onChange={(e) => handleProfileChange('currency', e.target.value)}
                        disabled={!isPmpAdmin}
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Financial Details
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <TextField
                      fullWidth
                      label="PAN No"
                      value={profileForm.pan_no}
                      onChange={(e) => handleProfileChange('pan_no', e.target.value)}
                      disabled={!isPmpAdmin}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <FormControl fullWidth>
                      <InputLabel>Billing Currency</InputLabel>
                      <Select
                        value={profileForm.billing_currency || 'USD'}
                        label="Billing Currency"
                        onChange={(e) => handleProfileChange('billing_currency', e.target.value)}
                        disabled={!isPmpAdmin}
                      >
                        {currencies.map((currency) => (
                          <MenuItem key={currency.value} value={currency.value}>
                            {currency.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  Address Information
                </Typography>

                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={profileForm.address_1}
                  onChange={(e) => handleProfileChange('address_1', e.target.value)}
                  disabled={!isPmpAdmin}
                />

                <TextField
                  fullWidth
                  label="Address Line 2"
                  value={profileForm.address_2}
                  onChange={(e) => handleProfileChange('address_2', e.target.value)}
                  disabled={!isPmpAdmin}
                />

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                    <TextField
                      fullWidth
                      label="City"
                      value={profileForm.city}
                      onChange={(e) => handleProfileChange('city', e.target.value)}
                      disabled={!isPmpAdmin}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                    <TextField
                      fullWidth
                      label="State"
                      value={profileForm.state}
                      onChange={(e) => handleProfileChange('state', e.target.value)}
                      disabled={!isPmpAdmin}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 200px', minWidth: '150px' }}>
                    <TextField
                      fullWidth
                      label="ZIP Code"
                      value={profileForm.zip_code}
                      onChange={(e) => handleProfileChange('zip_code', e.target.value)}
                      disabled={!isPmpAdmin}
                    />
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={profileForm.country}
                      onChange={(e) => handleProfileChange('country', e.target.value)}
                      disabled={!isPmpAdmin}
                    />
                  </Box>
                  <Box sx={{ flex: '1 1 300px', minWidth: '250px' }}>
                    <TextField
                      fullWidth
                      label="PAN No"
                      value={profileForm.pan_no}
                      onChange={(e) => handleProfileChange('pan_no', e.target.value)}
                      disabled={!isPmpAdmin}
                    />
                  </Box>
                </Box>

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving || !isPmpAdmin}
                    startIcon={<Save />}
                  >
                    {saving ? 'Saving...' : 'Save Profile'}
                  </Button>
                </Box>
              </Box>
            </form>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Typography variant="h5" gutterBottom>
              Change Password
            </Typography>
            <form onSubmit={handlePasswordSubmit}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                <TextField
                  required
                  fullWidth
                  label="Current Password"
                  type={showPasswords.current ? 'text' : 'password'}
                  value={passwordForm.current_password}
                  onChange={(e) => handlePasswordChange('current_password', e.target.value)}
                  error={!!passwordFieldErrors.current_password}
                  helperText={passwordFieldErrors.current_password ? passwordFieldErrors.current_password[0] : ''}
                  inputRef={(el) => passwordFieldRefs.current.current_password = el}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('current')}
                          edge="end"
                        >
                          {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  required
                  fullWidth
                  label="New Password"
                  type={showPasswords.new ? 'text' : 'password'}
                  value={passwordForm.new_password}
                  onChange={(e) => handlePasswordChange('new_password', e.target.value)}
                  error={!!passwordFieldErrors.new_password}
                  helperText={passwordFieldErrors.new_password ? passwordFieldErrors.new_password[0] : "Password must be at least 8 characters with mixed case, numbers, and symbols"}
                  inputRef={(el) => passwordFieldRefs.current.new_password = el}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('new')}
                          edge="end"
                        >
                          {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <TextField
                  required
                  fullWidth
                  label="Confirm New Password"
                  type={showPasswords.confirm ? 'text' : 'password'}
                  value={passwordForm.new_password_confirmation}
                  onChange={(e) => handlePasswordChange('new_password_confirmation', e.target.value)}
                  error={!!passwordFieldErrors.new_password_confirmation}
                  helperText={passwordFieldErrors.new_password_confirmation ? passwordFieldErrors.new_password_confirmation[0] : ''}
                  inputRef={(el) => passwordFieldRefs.current.new_password_confirmation = el}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => togglePasswordVisibility('confirm')}
                          edge="end"
                        >
                          {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />

                <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
                  <Button
                    type="submit"
                    variant="contained"
                    disabled={saving}
                    startIcon={<Lock />}
                  >
                    {saving ? 'Changing...' : 'Change Password'}
                  </Button>
                </Box>
              </Box>
            </form>
          </TabPanel>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={handleCloseSnackbar}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: '100%' }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Container>
  );
};

export default ProfileEdit;
